/**
 * The three steps to a first image, said before anything else.
 *
 * A visitor met the whole generation form (Sampler, CFG, Seed…) with nothing
 * saying what this site is, that it needs a program, or where to start; a
 * signed-in person without a computer met the same form (audit 03/10). Until
 * a computer is connected the form cannot do anything anyway, so this takes
 * its place, ticks each step as it is done, and steps aside on its own.
 */
import { h, setChildren } from "../../components/dom.js";
import { serverGenerates } from "../../services/execution.js";
import { store } from "../../state/store.js";

export interface GettingStarted {
	element: HTMLElement;
	/** Whether the three steps are on screen (the form waits behind them). */
	isShown(): boolean;
}

/** Opens a workspace the way its rail button does. */
function openWorkspace(id: string): void {
	document.querySelector<HTMLButtonElement>(`.rail-item[data-workspace="${id}"]`)?.click();
}

export function createGettingStarted(onChange: () => void): GettingStarted {
	const element = h("section", { class: "first-image-card getting-started", hidden: true });

	function step(
		done: boolean,
		current: boolean,
		title: string,
		detail: string,
		action?: HTMLElement
	) {
		return h(
			"li",
			{ class: done ? "step done" : current ? "step current" : "step" },
			h("strong", null, `${done ? "✓" : current ? "→" : "·"} ${title}`),
			done ? null : h("p", { class: "help-text" }, detail),
			!done && current && action ? action : null
		);
	}

	function render(): void {
		const state = store.get();
		const signedIn = Boolean(state.user);
		const known = state.workers ?? [];
		const online = known.some((worker) => worker.online !== false);
		const shown = !serverGenerates() && !(signedIn && online);
		const changed = element.hidden === shown;
		element.hidden = !shown;
		if (shown) {
			const toAccount = (label: string) =>
				h(
					"button",
					{ class: "btn btn-primary", type: "button", onclick: () => openWorkspace("account") },
					label
				);
			setChildren(
				element,
				h("h3", null, "Bienvenue !"),
				h(
					"p",
					null,
					"Ce site crée des images avec votre propre ordinateur : gratuitement, et vos images " +
						"restent chez vous. Trois étapes, une seule fois :"
				),
				h(
					"ol",
					{ class: "steps" },
					step(
						signedIn,
						!signedIn,
						"Créez votre compte (gratuit)",
						"Une adresse email et un mot de passe suffisent.",
						toAccount("Créer mon compte ou me connecter")
					),
					step(
						signedIn && online,
						signedIn && !online,
						"Installez le programme sur votre ordinateur",
						known.length > 0
							? "Votre ordinateur est relié mais le programme est fermé : ouvrez-le, il se reconnecte tout seul."
							: "Téléchargez-le (~40 Mo), ouvrez-le et cliquez « Relier avec mon navigateur » : ce site vous demandera de confirmer. Il prépare ensuite votre ordinateur tout seul.",
						toAccount("Télécharger le programme")
					),
					step(
						false,
						false,
						"Votre première image",
						"Le site vous propose un modèle adapté à votre ordinateur : un clic, et l'image arrive."
					)
				)
			);
		}
		// Never during construction: the caller is still assigning the object
		// this would hand it (a component calling back from its own constructor
		// took the whole shell down once, see CLAUDE.md). It asks for itself.
		if (changed && built) onChange();
	}

	// The status too: until it arrives, `serverGenerates()` says no, and a
	// server that generates itself kept its form hidden behind these steps.
	store.subscribe(
		(s) =>
			`${s.status?.generates_here === true}:${s.user ? "in" : "out"}:${(s.workers ?? []).map((w) => `${w.worker_id}:${w.online}`).join(",")}`,
		render
	);
	let built = false;
	render();
	built = true;
	return { element, isShown: () => !element.hidden };
}
